import { Button, Callout, Flex, IconButton, Spinner, Text } from '@radix-ui/themes';
import { Check, Download, FileQuestion, Send, X } from 'lucide-react';
import { MAX_FILE_BYTES } from '@mac-remote/protocol';
import { MIME, decodeText, ext, fileKind, humanSize } from '../lib/fileKind';

export interface PreviewFile {
  name: string;
  /** Absolute path on the Mac. Needed to ask for the file again — to send it to the
   *  chat, which happens Mac→broker→Telegram and never through the phone. */
  path: string;
  size: number | null;
  /** null while the bytes are still coming, and forever for a file we never asked for
   *  because we could not have drawn it. */
  b64: string | null;
  /** Why there will be no bytes. Without this a failed read left the sheet spinning
   *  under a dismissed alert, with nothing to do but close it and wonder. */
  error?: string | null;
}

interface Props {
  file: PreviewFile;
  /** True while this file is on its way to the chat. */
  sending: boolean;
  /** Set once it arrives, so the sheet can say so without a modal alert. */
  sent: boolean;
  /** Whether a browser download can work here at all — false in the iOS/Android
   *  WebView, where a blob URL blanks the whole app. */
  canDownload: boolean;
  onClose: () => void;
  onDownload: () => void;
  onSendToChat: () => void;
}

/**
 * In-app file viewer.
 *
 * Two facts shape it. A Telegram WebView cannot save a file — navigating one to a blob
 * URL turns it black — so on a phone the way out is the chat, not a download. And the
 * bytes only travel when they can be drawn: images and text open here, everything else
 * (PDF, zip, docx, video) goes straight to Telegram, which has a real viewer for it.
 */
export function FilePreview({
  file,
  sending,
  sent,
  canDownload,
  onClose,
  onDownload,
  onSendToChat,
}: Props) {
  const e = ext(file.name);
  const kind = fileKind(file.name, file.size);
  const waiting = kind !== 'other' && file.b64 === null && !file.error;
  // Checked here rather than only on the Mac: the size is already on screen, so refusing
  // before the request is both faster and more honest than a round trip that cannot work.
  const tooBig = file.size !== null && file.size > MAX_FILE_BYTES;

  return (
    <Flex
      direction="column"
      style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'var(--app-bg)' }}
    >
      <Flex align="center" justify="between" px="3" py="2" gap="2" style={{ borderBottom: '1px solid var(--gray-a4)' }}>
        <Flex direction="column" style={{ minWidth: 0, flex: 1 }}>
          <Text size="2" weight="bold" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {file.name}
          </Text>
          {file.size !== null && (
            <Text size="1" style={{ color: 'var(--app-hint)' }}>
              {humanSize(file.size)}
            </Text>
          )}
        </Flex>
        <Flex align="center" gap="2" style={{ flex: '0 0 auto' }}>
          {/* Primary everywhere, because it is the only one that works everywhere. */}
          <Button size="1" variant="solid" color="jade" disabled={sending || tooBig} onClick={onSendToChat}>
            {sending ? <Spinner size="1" /> : sent ? <Check size={13} /> : <Send size={13} />}
            {sending ? 'Отправляю' : sent ? 'В чате' : 'В чат'}
          </Button>
          {/* Only where a download is physically possible: Telegram Desktop, Web, or a
              plain browser. On iOS/Android the parent hides it rather than offering a
              button that opens a black screen. */}
          {canDownload && file.b64 !== null && (
            <Button size="1" variant="soft" color="gray" onClick={onDownload}>
              <Download size={13} /> Скачать
            </Button>
          )}
          <IconButton size="1" variant="ghost" color="gray" onClick={onClose}>
            <X size={18} />
          </IconButton>
        </Flex>
      </Flex>

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', background: kind === 'image' ? '#000' : 'var(--app-bg)' }}>
        {file.error ? (
          <Flex direction="column" align="center" justify="center" gap="3" style={{ height: '100%', padding: 24 }}>
            <Callout.Root color="amber" size="1">
              <Callout.Text>{file.error}</Callout.Text>
            </Callout.Root>
          </Flex>
        ) : waiting ? (
          <Flex align="center" justify="center" gap="3" style={{ height: '100%' }}>
            <Spinner size="2" />
            <Text size="2" style={{ color: 'var(--app-hint)' }}>
              Тяну файл с мака…
            </Text>
          </Flex>
        ) : kind === 'image' ? (
          <Flex align="center" justify="center" style={{ minHeight: '100%', padding: 8 }}>
            <img
              src={`data:${MIME[e] ?? 'image/png'};base64,${file.b64}`}
              alt={file.name}
              style={{ maxWidth: '100%', height: 'auto', borderRadius: 6 }}
            />
          </Flex>
        ) : kind === 'text' ? (
          <pre
            style={{
              margin: 0, padding: 12, fontSize: 12, lineHeight: 1.5,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'var(--app-text)',
            }}
          >
            {decodeText(file.b64 ?? '')}
          </pre>
        ) : (
          <Flex direction="column" align="center" justify="center" gap="3" style={{ height: '100%', padding: 24, textAlign: 'center' }}>
            <FileQuestion size={40} color="var(--gray-8)" />
            <Text size="2" style={{ color: 'var(--app-hint)' }}>
              {e ? `.${e}` : 'Такой файл'} внутри приложения не показать
              {e === 'pdf' ? ' — WebView Telegram не умеет PDF.' : '.'}
            </Text>
            <Text size="2" style={{ color: 'var(--app-hint)' }}>
              Нажми «В чат» — бот пришлёт файл в переписку, там его откроет сам телефон, и
              оттуда же можно сохранить или переслать.
            </Text>
            {/* No size check here: a file over the ceiling never reaches this branch —
                the sheet opens with `error` set instead, which says so in one place. */}
            <Button size="2" color="jade" disabled={sending} onClick={onSendToChat}>
              {sending ? <Spinner size="1" /> : sent ? <Check size={15} /> : <Send size={15} />}
              {sending ? 'Отправляю…' : sent ? 'Уже в чате' : 'В чат'}
            </Button>
          </Flex>
        )}
      </div>
    </Flex>
  );
}
